import React from 'react'
import { PageData } from '@/api/Pagination/Types'
import { RewardProgramDetailsUpdateDto } from '@/api/Trading-Accounts/Types/RewardProgramDto'
import { RewardProgramDto, RewardsHubDto } from '@/api/Trading-Accounts/Types/RewardsHubDto'
import { DataCard } from '@/components/DataCard/DataCard'
import {
  FormDateField,
  FormField,
  FormMultiSelectField,
  FormSelectField,
} from '@/components/Form/FormField'
import { LoadingOverlay } from '@/components/LoadingOverlay/LoadingOverlay'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useRewardProgramOptions } from '@/routes/Marketing/RewardsHub/RewardPrograms/useRewardProgramOptions'
import { FormSubmitValues } from '@/utils/formValidation'
import { FormikProps, withFormik } from 'formik'

import { validationSchema } from './RewardProgramValidationSchemaCreate'

interface OuterProps {
  isOpen?: boolean
  onClose(): void
  onSubmit(values: FormSubmitValues<RewardProgramDetailsUpdateDto>): Promise<void>
  rewardsList?: PageData<RewardsHubDto[]>
  rewardProgramList?: PageData<RewardProgramDto[]>
}

export const AddNewRewardProgramDialogUI: React.FC<
  FormikProps<RewardProgramDetailsUpdateDto> & OuterProps
> = (props) => {
  const { handleSubmit, isSubmitting, setFieldValue, isValid, values, rewardsList } = props

  const {
    statusOptions,
    companyOptions,
    countryOptions,
    currencyOptions,
    rewardProgramTierLogicalOperatorsOptions,
    metricsOptions,
    conditionalLogicOptions,
    rewardsOptions,
    rewardCadenceOptions,
  } = useRewardProgramOptions(rewardsList)

  const handleSubmitForm = () => {
    handleSubmit()
  }

  const newTier = () => ({
    name: '',
    trackingMetricId: 0,
    rewardProgramTierConditionLogicId: 0,
    rewardId: '',
    callToActionText: '',
    metrics: [],
    currencyId: null,
  })

  const handleAddTier = () => {
    const updated = [...(values.tiers ?? []), newTier()]
    setFieldValue('tiers', updated)
    setFieldValue('numberOfTiers', updated.length)
  }

  const getConditionalLogicOptions = (trackingMetricId: number) => {
    if (trackingMetricId === 1) {
      return conditionalLogicOptions.filter((option) => option.id === 1)
    }
    return conditionalLogicOptions
  }

  const handleRemoveTier = (i: number) => {
    const updated = (values.tiers ?? []).filter((_, idx) => idx !== i)
    setFieldValue('tiers', updated)
    setFieldValue('numberOfTiers', updated.length)
  }

  const newMetric = () => ({
    trackingMetricId: 0,
    rewardProgramTierConditionLogicId: 0,
    rewardProgramTierLogicalOperatorId: 1,
    value: 0,
  })

  const handleRemoveCondition = (tierIndex: number, metricIndex: number) => {
    const current = values.tiers?.[tierIndex]?.metrics ?? []
    setFieldValue(
      `tiers.${tierIndex}.metrics`,
      current.filter((_, i) => i !== metricIndex)
    )
  }

  const handleAddCondition = (tierIndex: number) => {
    const current = values.tiers?.[tierIndex]?.metrics ?? []
    const sharedCurrency = current[0]?.currencyId ?? null
    const sharedOperator = current[0]?.rewardProgramTierLogicalOperatorId ?? 1
    setFieldValue(`tiers.${tierIndex}.metrics`, [
      ...current,
      {
        ...newMetric(),
        currencyId: sharedCurrency,
        rewardProgramTierLogicalOperator: sharedOperator,
      },
    ])
  }

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent scrollable className='max-w-[1000px]'>
        <DialogHeader>
          <DialogTitle>Create New Program</DialogTitle>
        </DialogHeader>
        <div>
          <DataCard title='Marketing Campaigns Options' columns={1}>
            <FormField name='internalName' label='Program Name (internal)' />
            <FormField name='displayName' label='Display Name (client facing)' />
            <FormField name='description' label='Description (client facing)' />
            <FormMultiSelectField
              name='companies'
              label='Entities'
              value={values.companies}
              options={companyOptions}
              onChange={(values) => {
                const comps = Array.isArray(values)
                  ? values.map((v) => Number(v))
                  : values == null
                    ? []
                    : [Number(values)]
                setFieldValue('companies', comps)
              }}
            />

            <FormMultiSelectField
              name='countries'
              label='Countries'
              value={values.countries}
              options={countryOptions}
              onChange={(values) => {
                const countries = Array.isArray(values)
                  ? values
                  : values == null
                    ? []
                    : [Number(values)]
                setFieldValue('countries', countries)
              }}
            />
            <FormSelectField
              name='rewardStatusId'
              label='Program Status'
              options={statusOptions}
              onChange={(value) => setFieldValue('rewardStatusId', Number(value))}
            />
            <FormField name='displayOrder' label='Display Order' />
            <FormDateField name='startDate' label='Start Date' showTimeSelect />
            <FormDateField name='endDate' label='End Date' showTimeSelect />
            <FormSelectField
              name='resetCadenceId'
              label='Reset Cadence'
              options={rewardCadenceOptions}
              onChange={(value) => setFieldValue('resetCadenceId', Number(value))}
            />
          </DataCard>
        </div>
        <div>
          {values.tiers?.map((tier, index) => (
            <DataCard title={'Tier and Reward configuration'}>
              <DataCard key={index} title={''}>
                <FormField key={index} name={`tiers.${index}.name`} label={`Tier ${index + 1}`} />
                <FormSelectField
                  value={tier.trackingMetricId}
                  name={`tiers.${index}.trackingMetricId`}
                  options={metricsOptions}
                  label={'Tracking Metric'}
                  onChange={(value) => {
                    const numValue = Number(value)
                    setFieldValue(`tiers.${index}.trackingMetricId`, numValue)
                    if (numValue !== 1 && !tier.currencyId) {
                      const existingCurrency = tier.metrics.find((m) => m.currencyId)?.currencyId
                      if (existingCurrency) {
                        setFieldValue(`tiers.${index}.currencyId`, existingCurrency)
                      }
                    }
                  }}
                />
                <FormSelectField
                  value={tier.rewardProgramTierConditionLogicId}
                  name={`tiers.${index}.rewardProgramTierConditionLogicId`}
                  options={getConditionalLogicOptions(tier.trackingMetricId)}
                  label={'Conditional Logic'}
                  onChange={(value) =>
                    setFieldValue(`tiers.${index}.rewardProgramTierConditionLogicId`, Number(value))
                  }
                />
                <FormField
                  label={'Threshold Value'}
                  name={`tiers.${index}.thresholdValue`}
                  value={tier.thresholdValue}
                  onChange={(e) =>
                    setFieldValue(`tiers.${index}.thresholdValue`, Number(e.target.value))
                  }
                />
                <FormSelectField
                  name={`tiers.${index}.rewardId`}
                  value={values.tiers?.[index]?.rewardId}
                  label='Reward to claim'
                  options={rewardsOptions}
                  onChange={(value) => {
                    setFieldValue(`tiers.${index}.rewardId`, value)
                  }}
                />
                <FormField
                  name={`tiers.${index}.callToActionText`}
                  label='Button Text (CTA)'
                  value={tier.callToActionText}
                />
                {values.tiers && (
                  <div className='flex justify-center gap-2'>
                    <Tooltip>
                      <TooltipTrigger>
                        <span
                          style={{
                            pointerEvents: (tier.metrics ?? []).length >= 3 ? 'auto' : undefined,
                          }}
                        >
                          <Button
                            variant='ghost'
                            className='text-xs px-3 py-1 border border-dashed border-gray-400 rounded bg-transparent cursor-pointer'
                            onClick={() => handleAddCondition(index)}
                            disabled={(tier.metrics ?? []).length >= 3}
                          >
                            + Add Condition
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {(tier.metrics ?? []).length >= 3 && (
                        <TooltipContent side='bottom'>
                          Maximum of 3 conditions per tier reached.
                        </TooltipContent>
                      )}
                    </Tooltip>
                    <Button
                      variant='ghost'
                      className='text-xs px-3 py-1 border border-dashed border-red-400 rounded bg-transparent cursor-pointer text-red-500'
                      onClick={() => handleRemoveTier(index)}
                    >
                      × Remove Tier {index + 1}
                    </Button>
                  </div>
                )}

                {(tier.metrics ?? []).length > 0 && (
                  <FormSelectField
                    name={`tiers.${index}.logicalOperatorId`}
                    label=''
                    value={tier.metrics?.[0]?.rewardProgramTierLogicalOperatorId ?? 1}
                    options={rewardProgramTierLogicalOperatorsOptions}
                    onChange={(value) =>
                      tier.metrics.forEach((_, i) =>
                        setFieldValue(
                          `tiers.${index}.metrics.${i}.rewardProgramTierLogicalOperatorId`,
                          Number(value)
                        )
                      )
                    }
                  />
                )}

                {[tier.trackingMetricId, ...tier.metrics.map((m) => m.trackingMetricId)].some(
                  (id) => id !== 1 && id !== 0
                ) && (
                  <FormSelectField
                    name={`tiers.${index}.currencyId`}
                    label='Currency'
                    // value={tier.currencyId ?? undefined}
                    value={
                      tier.trackingMetricId !== 1
                        ? (tier.currencyId ?? undefined)
                        : (tier.metrics.find(
                            (m) => m.trackingMetricId !== 1 && m.trackingMetricId !== 0
                          )?.currencyId ?? undefined)
                    }
                    options={currencyOptions}
                    onChange={(value) => {
                      if (tier.trackingMetricId !== 1) {
                        setFieldValue(`tiers.${index}.currencyId`, value)
                      }
                      // Always write to metrics that need currency, regardless of tier's trackingMetric
                      tier.metrics.forEach((metric, metricIndex) => {
                        if (metric.trackingMetricId !== 1 && metric.trackingMetricId !== 0) {
                          setFieldValue(`tiers.${index}.metrics.${metricIndex}.currencyId`, value)
                        }
                      })
                    }}
                  />
                )}

                {tier.metrics.map((metric, metricIndex) => (
                  <>
                    <FormSelectField
                      name={`tiers.${index}.metrics.${metricIndex}.trackingMetricId`}
                      value={metric.trackingMetricId}
                      options={metricsOptions}
                      label={`Tracking Metric ${metricIndex + 1}`}
                      onChange={(value) => {
                        const numValue = Number(value)
                        setFieldValue(
                          `tiers.${index}.metrics.${metricIndex}.trackingMetricId`,
                          numValue
                        )
                        if (numValue === 1) {
                          setFieldValue(
                            `tiers.${index}.metrics.${metricIndex}.rewardProgramTierConditionLogicId`,
                            1
                          )
                        } else {
                          const sharedCurrency =
                            tier.trackingMetricId !== 1
                              ? tier.currencyId
                              : tier.metrics.find((m, i) => i !== metricIndex && m.currencyId)
                                  ?.currencyId
                          if (sharedCurrency) {
                            setFieldValue(
                              `tiers.${index}.metrics.${metricIndex}.currencyId`,
                              sharedCurrency
                            )
                          }
                        }
                      }}
                    />
                    <FormSelectField
                      name={`tiers.${index}.metrics.${metricIndex}.rewardProgramTierConditionLogicId`}
                      value={metric.rewardProgramTierConditionLogicId}
                      options={getConditionalLogicOptions(metric.trackingMetricId)}
                      label={''}
                      onChange={(value) =>
                        setFieldValue(
                          `tiers.${index}.metrics.${metricIndex}.rewardProgramTierConditionLogicId`,
                          Number(value)
                        )
                      }
                    />
                    <FormField
                      label={''}
                      name={`tiers.${index}.metrics.${metricIndex}.value`}
                      value={metric.value}
                      onChange={(e) =>
                        setFieldValue(
                          `tiers.${index}.metrics.${metricIndex}.value`,
                          Number(e.target.value)
                        )
                      }
                    />

                    <div className='flex justify-center'>
                      <Button
                        variant='ghost'
                        className='text-xs px-3 py-1 border border-dashed border-red-400 rounded bg-transparent cursor-pointer text-red-500'
                        onClick={() => handleRemoveCondition(index, metricIndex)}
                      >
                        × Remove Condition {metricIndex + 1}
                      </Button>
                    </div>
                  </>
                ))}
              </DataCard>
            </DataCard>
          ))}
        </div>
        <Button
          className='w-fit inline-flex text-sm px-3 py-1'
          type='button'
          onClick={handleAddTier}
        >
          + Add Tier
        </Button>

        <DialogFooter>
          <Button
            onClick={handleSubmitForm}
            disabled={isSubmitting || !isValid || values.tiers.length === 0}
            type='button'
          >
            Save
          </Button>
        </DialogFooter>
        <LoadingOverlay isLoading={isSubmitting} />
      </DialogContent>
    </Dialog>
  )
}
export const AddNewRewardProgram = withFormik<OuterProps, RewardProgramDetailsUpdateDto>({
  validationSchema: (props: OuterProps) =>
    validationSchema(props.rewardProgramList?.items?.length ?? 0),
  enableReinitialize: true,
  validateOnMount: true,
  mapPropsToValues: () => ({
    tiers: [],
    internalName: '',
    displayName: '',
    description: '',
    displayOrder: 0,
    startDate: '',
    endDate: '',
    companies: [],
    countries: [],
    resetCadenceId: 0,
    rewardStatusId: 0,
  }),
  handleSubmit: async (values, { props, setSubmitting, resetForm }) => {
    try {
      await props.onSubmit(values)
      resetForm()
    } finally {
      setSubmitting(false)
    }
  },
})(AddNewRewardProgramDialogUI)
